import React, { useCallback, useEffect, useRef } from "react";
import { Container } from "@pixi/react";
import { useSelector, useDispatch } from "react-redux";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import anime from "animejs";
import { attemptFullscreenToggle } from "../../../../utilities";
import LoaderController from "./LoaderController";
import { setLoadingSpineHandoverTime } from "../../shared/Popups/IntroPopup/IntroPopup/IntroPopupComposedController";
import { isStandaloneReplayMode } from "../../../../utils/launcherOptions";
import {
  setLoaderComplete,
  setLoaderFadedOut,
  setLoaderFadeMidpoint,
  setIntroMounted,
} from "../../../../State/Slices/loadingProgressSlice";

// ── DEV: freeze the game on the loading screen ───────────────────────────────
// While the Mad Labs preloader artwork is being built, keep the loader up for
// good: the spine keeps looping, the hand-off to the intro popup never fires,
// and the game tree never mounts behind it. Assets still download normally in
// the background, so nothing errors — the game simply never reveals itself.
// Set back to false to restore the normal loader → intro → game sequence.
// const DEV_FREEZE_LOADER = true; // ON for sizing the loader logo (2026-10-05)
const DEV_FREEZE_LOADER = false; // off again — logo sized (2026-10-05)

// Shortest time the loader stays on screen before it hands over.
//
// The hand-off no longer waits for the wordmark to reach a loop boundary (the
// intro popup continues the same revolution, so there is no seam to hide), and
// without a floor a warm cache could flash this screen past in a few frames.
const MIN_LOADER_VISIBLE_MS = 1200;

const Loader = () => {
  const { dimensions, uiSymbolHeight, uiSymbolSize, device, orientation, isMobileSmall } =
    useDimensions();
  const loadingProgress = useSelector((state) => state.loadingProgress);
  const dispatch = useDispatch();
  const containerRef = useRef(null);
  const loaderControllerRef = useRef(null);
  const fadeOutStartedRef = useRef(false);
  const errorShownRef = useRef(false);
  const handleBackgroundClickRef = useRef(null);
  const mountedAtRef = useRef(performance.now());
  const handOffTimeoutRef = useRef(null);

  // Background click: fullscreen only (mirrors IntroPopupComposed)
  const handleBackgroundClick = useCallback(
    async (event) => {
      if (event?.stopPropagation) event.stopPropagation();
      await attemptFullscreenToggle(dispatch, device);
    },
    [dispatch, device],
  );
  handleBackgroundClickRef.current = handleBackgroundClick;

  // Initialize spine controller on mount
  useEffect(() => {
    if (
      containerRef.current &&
      !loaderControllerRef.current &&
      dimensions &&
      uiSymbolHeight
    ) {
      loaderControllerRef.current = new LoaderController(
        containerRef.current,
        dimensions,
        uiSymbolHeight,
        { device, orientation, isMobileSmall, uiSymbolSize }
      );
      loaderControllerRef.current.setBackgroundClickHandler((...args) => {
        if (handleBackgroundClickRef.current) {
          handleBackgroundClickRef.current(...args);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update spine when dimensions change
  useEffect(() => {
    if (loaderControllerRef.current) {
      loaderControllerRef.current.updateDimensions(
        dimensions,
        uiSymbolHeight,
        { device, orientation, isMobileSmall, uiSymbolSize }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dimensions, uiSymbolHeight, uiSymbolSize, device, orientation]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (loaderControllerRef.current) {
        loaderControllerRef.current.cleanup();
        loaderControllerRef.current = null;
      }
    };
  }, []);

  // Init failed — stand down.
  //
  // The error UI is no longer the loader's job: LoadingErrorPopup is its own
  // component, mounted by Stage on the error state and sitting above this at
  // zIndex 10002. All that is left here is to stop: kill the fade if one is
  // running, drop the spine, and get off the screen. Everything else that
  // would have carried the sequence forward is blocked at the reducer.
  useEffect(() => {
    if (loadingProgress.status !== "error" || errorShownRef.current) return;
    errorShownRef.current = true;

    // A hand-off may already be counting down — it must not mount the intro
    // popup on top of a dead session.
    if (handOffTimeoutRef.current) {
      clearTimeout(handOffTimeoutRef.current);
      handOffTimeoutRef.current = null;
    }

    if (containerRef.current) {
      anime.remove(containerRef.current);
      containerRef.current.visible = false;
    }
    loaderControllerRef.current?.stop();
  }, [loadingProgress.status]);

  // Hand-off to the intro popup.
  //
  // The loader no longer waits for the game to finish loading. It leaves as
  // soon as its spine has completed one full revolution AND the intro's own
  // force-loaded assets have landed (whichever is later) — everything still
  // downloading does so behind the intro popup, where the player is reading
  // the feature slider instead of watching a spinner.
  //
  // controller.onLoadingComplete() gives that "whichever is later" for free:
  // it resolves immediately if the first loop already finished, otherwise the
  // spine's own complete listener resolves it. Only the trigger changed —
  // introAssetsReady instead of a fully loaded game.
  //
  // The mount-behind-an-opaque-layer trick is unchanged, the opaque layer is
  // just the intro popup now instead of the game: mount, let it settle, then
  // fade. setLoaderComplete (which mounts the actual game tree) has moved to
  // its own effect below, since that now happens much later.
  //
  // Standalone replay links are the exception: Stage.js deliberately doesn't
  // mount the intro popup there, so leaving early would drop the player onto a
  // bare stage with nothing to look at until the game mounts. That mode keeps
  // the original behaviour — hold the loader until everything is ready.
  useEffect(() => {
    if (DEV_FREEZE_LOADER) return;
    // Init failed. introAssetsReady is set by a DIFFERENT thunk and is very
    // likely already true, so without this the loader would hand over and
    // mount the intro popup on top of a dead session — which is exactly the
    // bug this guard exists for.
    if (loadingProgress.status === "error") return;

    const readyToHandOff = isStandaloneReplayMode()
      ? loadingProgress.status === "success"
      : loadingProgress.introAssetsReady;

    if (readyToHandOff && containerRef.current && !fadeOutStartedRef.current) {
      fadeOutStartedRef.current = true;

      const controller = loaderControllerRef.current;
      if (!controller) {
        // No controller — skip animation, just mount and hide.
        // Still emit BOTH loader lifecycle signals: there's no fade tween
        // here to emit them from, and anything waiting on them would other-
        // wise hang forever (the standalone replay link's Enter modal is
        // gated on the midpoint — without this it would never open, leaving
        // a replay-locked page with no way to start the round).
        if (containerRef.current) containerRef.current.visible = false;
        dispatch(setIntroMounted());
        dispatch(setLoaderFadeMidpoint());
        dispatch(setLoaderFadedOut());
        return;
      }

      // Step 1: decide when the loader is allowed to leave.
      //
      // It used to wait here for the wordmark to reach a loop boundary and
      // then freeze it, because the intro popup built a SECOND spine from
      // scratch and the two had to meet somewhere. They no longer do: the
      // popup picks the same revolution up frame-for-frame (see
      // setLoadingSpineHandoverTime), so the fade can happen at any point in
      // the animation and the player cannot tell. That removes up to a full
      // revolution of dead time here, and the popup then only has to finish
      // the revolution already in progress instead of a fresh one — the two
      // stacked ~2.9s waits that made entering the game feel stuck.
      //
      // Standalone replay links get the old behaviour: Stage.js mounts no
      // intro popup there, so there is nothing to hand the wordmark to and
      // cutting it mid-revolution would be visible.
      const beginHandOff = () => {
        if (!containerRef.current || errorShownRef.current) return;

        // Step 2: mount the intro popup behind the loader
        dispatch(setIntroMounted());

        // Step 3: Wait for the intro to settle (count stable frames after mount)
        const SETTLE_FRAMES = 10;
        let stableCount = 0;

        const waitForSettle = () => {
          stableCount++;
          if (stableCount < SETTLE_FRAMES) {
            requestAnimationFrame(waitForSettle);
            return;
          }

          // Step 4: Intro has settled — fade out the loader
          if (!containerRef.current) return;
          let midpointFired = false;
          anime({
            targets: containerRef.current,
            alpha: [1, 0],
            duration: 300,
            easing: "easeOutQuad",
            // Halfway through the fade — the intro underneath is already
            // half-revealed. Anything that should appear AS the loader
            // leaves (rather than after it, which reads as a gap) hooks
            // this instead of setLoaderFadedOut below. Tied to the real
            // animation's progress, not a guessed delay, so it stays
            // correct if the fade duration ever changes.
            update: (anim) => {
              if (midpointFired || anim.progress < 50) return;
              midpointFired = true;
              dispatch(setLoaderFadeMidpoint());
            },
            complete: () => {
              if (containerRef.current) {
                containerRef.current.visible = false;
              }
              // Free the spine but KEEP the controller — it still owns the
              // error UI, and a rejection can arrive long after this point.
              if (loaderControllerRef.current) {
                loaderControllerRef.current.releaseSpine();
              }
              // Signal that the loader is visually gone
              dispatch(setLoaderFadedOut());
            },
          });
        };

        requestAnimationFrame(waitForSettle);
      };

      if (isStandaloneReplayMode()) {
        controller.onLoadingComplete().then(beginHandOff);
        return;
      }

      const remaining = Math.max(
        0,
        MIN_LOADER_VISIBLE_MS - (performance.now() - mountedAtRef.current),
      );
      handOffTimeoutRef.current = setTimeout(() => {
        handOffTimeoutRef.current = null;
        // Published as late as possible, so the popup's spine starts on the
        // frame the loader's is actually on.
        setLoadingSpineHandoverTime(controller.getSpineTrackTime());
        beginHandOff();
      }, remaining);
    }
  }, [loadingProgress.introAssetsReady, loadingProgress.status, dispatch]);

  // Drop a pending hand-off if the loader goes away first (error, unmount).
  useEffect(
    () => () => {
      if (handOffTimeoutRef.current) {
        clearTimeout(handOffTimeoutRef.current);
        handOffTimeoutRef.current = null;
      }
    },
    [],
  );

  // Game tree mount — a fallback only.
  //
  // Normally the INTRO POPUP owns this dispatch: it freezes its spine first,
  // then mounts the game, so the mount's 270-360ms blocking frame lands on a
  // static image rather than a moving animation. Doing it from here would
  // race ahead of that freeze and reintroduce the flash.
  //
  // This covers the cases where no intro popup exists to do it — standalone
  // replay links (Stage.js never mounts one) and any path where the intro
  // failed to mount at all. Without it the game would never appear.
  // Keyed off the MODE, not off introMounted: on a warm cache the game can
  // finish loading before the spine's ~2.9s revolution ends, and gating on
  // introMounted would fire this mid-animation — mounting the game from here
  // and defeating the intro's own sequencing entirely.
  useEffect(() => {
    if (DEV_FREEZE_LOADER) return;
    if (loadingProgress.status !== "success") return;
    // status is never both, but keep the intent explicit alongside the guard
    // in the hand-off effect above.
    if (!isStandaloneReplayMode()) return; // the intro popup owns this
    dispatch(setLoaderComplete());
  }, [loadingProgress.status, dispatch]);

  return (
    <Container
      name="secondary_loader"
      ref={containerRef}
      x={0}
      y={0}
      zIndex={10001}
      sortableChildren
    />
  );
};

export default Loader;
